/**
 * AgeGateSheet — **가입 직전에** 만 14세를 확인한다 (OI-43).
 *
 * 소셜 인증이 끝나 "처음 보는 계정"(`NEED_AGE`)임이 드러난 순간에만 열린다. 이미 가입된
 * 사람은 이 시트를 보지 않는다 — 판정 근거와 자리 이동의 이유는 `lib/ageGate` 주석에 있다.
 * 이 파일은 화면만 맡는다.
 *
 * 미달이면 **가입만 막고 앱은 계속 쓰게 둔다**(2026-09-14 사용자 결정) — 비회원
 * 스캔 5회·검색이 이 서비스의 유입 경로라, 나이 때문에 앱이 벽으로 끝나면 안 된다.
 * 판정을 저장하지 않으므로 다시 시도하면 다시 묻는다(2026-09-15 사용자 결정).
 *
 * **입력이 아니라 선택이다**(2026-09-14 사용자 결정). 8자리를 손으로 치면 오타가
 * 그대로 판정이 된다 — 한 자리를 잘못 눌러 1912년이 되면 통과하고, 2102년이 되면
 * 멀쩡한 사용자가 미달로 막힌다. 되돌릴 길도 없다(판정은 localStorage 에 남는다).
 * 셀렉터 셋은 그 실수를 애초에 못 하게 만든다:
 *  - 연도는 올해부터 거슬러 {@link YEAR_SPAN}년,
 *  - 올해를 고르면 달은 이번 달까지, 이번 달을 고르면 날은 오늘까지 (미래 차단),
 *  - 날 수는 고른 연·월의 실제 말일까지 (2월 30일이 안 생긴다. 윤년 포함).
 *
 * ⚠ 지역 CSS 를 만들지 않았다 — Sheet + Astryx(VStack·HStack·StackItem·Heading·
 *   Text·Selector·Button) 조합으로 충분하다. 새 .module.css 는 승인 사항이다.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { HStack, StackItem, VStack } from "@astryxdesign/core/Layout";
import { Selector } from "@astryxdesign/core/Selector";
import Sheet from "../feedback/Sheet";
import { MIN_AGE, isEligible, parseBirthYmd } from "../../lib/ageGate";

/** 연도 목록의 길이. 올해 포함 101개. */
const YEAR_SPAN = 100;

/** 그 달의 말일. 연도를 같이 받는 이유는 윤년이다. */
function lastDayOf(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

interface AgeGateSheetProps {
  open: boolean;
  onClose: () => void;
  /** 통과 — 생년월일(YYYYMMDD)을 들고 가입 마무리(`completeSocialSignup`)로 잇는다. */
  onPassed: (birthYmd: string) => void;
}

export default function AgeGateSheet({ open, onClose, onPassed }: AgeGateSheetProps) {
  const { t, i18n } = useTranslation("common");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** 미달 판정 — 입력 화면을 안내 화면으로 바꾼다. */
  const [blocked, setBlocked] = useState(false);

  // 닫았다 다시 열면 처음부터. 미달 안내를 본 채로 다시 열리면 뭘 해야 할지 알 수 없다.
  useEffect(() => {
    if (!open) return;
    setYear("");
    setMonth("");
    setDay("");
    setError(null);
    setBlocked(false);
  }, [open]);

  // 열려 있는 동안 "오늘"을 고정한다 — 매 렌더 new Date() 면 목록이 계산될 때마다 흔들린다.
  const today = useMemo(() => new Date(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const years = useMemo(
    () => Array.from({ length: YEAR_SPAN + 1 }, (_, i) => String(today.getFullYear() - i)),
    [today],
  );

  // 달 이름은 로케일이 정한다 — ko "3월" · en "March" · ja "3月".
  const months = useMemo(() => {
    const format = new Intl.DateTimeFormat(i18n.language, { month: "long" });
    const max = year === String(today.getFullYear()) ? today.getMonth() + 1 : 12;
    return Array.from({ length: max }, (_, i) => ({
      value: String(i + 1),
      label: format.format(new Date(2000, i, 1)),
    }));
  }, [i18n.language, year, today]);

  const maxDay = useMemo(() => {
    if (!year || !month) return 31;
    const last = lastDayOf(Number(year), Number(month));
    const isThisMonth =
      year === String(today.getFullYear()) && month === String(today.getMonth() + 1);
    return isThisMonth ? Math.min(last, today.getDate()) : last;
  }, [year, month, today]);

  const days = useMemo(
    () => Array.from({ length: maxDay }, (_, i) => String(i + 1)),
    [maxDay],
  );

  // 앞 칸을 바꾸면 뒤 칸이 범위를 벗어날 수 있다 — 3월 31일에서 2월로 옮기는 경우.
  useEffect(() => {
    if (month && Number(month) > months.length) setMonth("");
  }, [month, months.length]);
  useEffect(() => {
    if (day && Number(day) > maxDay) setDay("");
  }, [day, maxDay]);

  const complete = Boolean(year && month && day);

  const handleSubmit = () => {
    const birthYmd = parseBirthYmd(
      `${year}${month.padStart(2, "0")}${day.padStart(2, "0")}`,
    );
    if (!birthYmd) {
      // 목록이 미래·말일을 이미 걸러 여기 오기 어렵지만, 판정 직전이라 조용히 넘기지 않는다.
      setError(t("auth.ageGate.invalid"));
      return;
    }
    // 미달이면 서버를 부르지 않는다 — 가입 대기표는 1회용이라, 어차피 거절될 요청으로
    // 태워버리면 사용자는 소셜 로그인부터 다시 해야 한다.
    if (!isEligible(birthYmd)) {
      setBlocked(true);
      return;
    }
    onPassed(birthYmd);
  };

  const pick = (set: (v: string) => void) => (v: string) => {
    set(v);
    if (error) setError(null);
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      ariaLabel={t("auth.ageGate.title")}
      backKind="age-gate-sheet"
    >
      <VStack gap={4} padding={4}>
        {blocked ? (
          <>
            <VStack gap={1}>
              <Heading level={3}>{t("auth.ageGate.blockedTitle")}</Heading>
              <Text type="supporting" color="secondary">
                {t("auth.ageGate.blockedBody", { age: MIN_AGE })}
              </Text>
            </VStack>
            <Button
              variant="primary"
              size="lg"
              label={t("auth.ageGate.blockedConfirm")}
              clickAction={onClose}
            />
          </>
        ) : (
          <>
            <VStack gap={1}>
              <Heading level={3}>{t("auth.ageGate.title")}</Heading>
              <Text type="supporting" color="secondary">
                {t("auth.ageGate.description", { age: MIN_AGE })}
              </Text>
            </VStack>

            {/* ⚠ `placement="above"` 셋 다 필수다. Selector 의 기본 배치는 고른 항목을
                트리거 위에 겹치는 방식인데, 시트는 화면 바닥에 붙어 있어 300px 목록이
                화면 아래로 61px 삐져나갔다(실측). 목록 자체는 top layer 라 시트의
                overflow:hidden 에 잘리지는 않는다 — 잘리는 건 화면이다. */}
            <HStack gap={2} align="start">
              <StackItem size="fill">
                <Selector
                  label={t("auth.ageGate.year")}
                  options={years}
                  value={year}
                  onChange={pick(setYear)}
                  placeholder={t("auth.ageGate.select")}
                  width="100%"
                  placement="above"
                />
              </StackItem>
              <StackItem size="fill">
                <Selector
                  label={t("auth.ageGate.month")}
                  options={months}
                  value={month}
                  onChange={pick(setMonth)}
                  placeholder={t("auth.ageGate.select")}
                  width="100%"
                  placement="above"
                />
              </StackItem>
              <StackItem size="fill">
                <Selector
                  label={t("auth.ageGate.day")}
                  options={days}
                  value={day}
                  onChange={pick(setDay)}
                  placeholder={t("auth.ageGate.select")}
                  width="100%"
                  placement="above"
                  status={error ? { type: "error", message: error } : undefined}
                />
              </StackItem>
            </HStack>

            <Button
              variant="primary"
              size="lg"
              label={t("auth.ageGate.submit")}
              isDisabled={!complete}
              clickAction={handleSubmit}
            />
          </>
        )}
      </VStack>
    </Sheet>
  );
}
